import {
  FunctionComponent,
  useCallback,
  useRef,
  useEffect,
  useState,
} from "react";
import { ExternalIcon } from "@icons/ExternalIcon";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { useAppDispatch, useAppSelector } from "@store/store";
import {
  selectIsPlaying,
  selectMediaFile,
  setMediaAction,
  setIsPlayingAction,
} from "@store/media/mediaReducer";
import { IconButton } from "@ui-kit/IconButton/IconButton";
import { Text } from "@ui-kit/Text/Text";
import { logError } from "@lib/logger";

import "./AudioPlayer.css";
import { AudioTranscript } from "./AudioTranscript";

const AudioPlayer: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const media = useAppSelector(selectMediaFile);
  const isPlaying = useAppSelector(selectIsPlaying);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationFrameRef = useRef<number>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);

  const handlePlayPause = useCallback(async () => {
    if (!audioRef.current) {
      return;
    }

    dispatch(setIsPlayingAction(!isPlaying));
  }, [isPlaying, dispatch]);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }

    if (isPlaying) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  const handleTranscriptPress = useCallback(() => {
    setIsTranscriptOpen(true);
  }, []);

  const handleTranscriptClose = useCallback(() => {
    setIsTranscriptOpen(false);
  }, []);

  useEffect(() => {
    let handlePlaying: () => void;
    let handlePause: () => void;
    let handleEnded: () => void;

    const playAudio = async () => {
      if (!media || media.type !== "AudioMediaFile") {
        return;
      }

      try {
        if (progressRef.current) {
          progressRef.current.classList.add("loading");
        }
        audioRef.current = new Audio(media.url);
        audioRef.current.preload = "auto";

        // Define handlers
        handlePlaying = () => {
          if (progressRef.current) {
            progressRef.current.classList.remove("loading");
          }
          dispatch(setIsPlayingAction(true));
        };

        handlePause = () => {
          dispatch(setIsPlayingAction(false));
        };

        handleEnded = () => {
          if (progressRef.current) {
            progressRef.current.style.width = "0%";
          }
          dispatch(setIsPlayingAction(false));
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
          }
        };

        audioRef.current.addEventListener("playing", handlePlaying);
        audioRef.current.addEventListener("pause", handlePause);
        audioRef.current.addEventListener("ended", handleEnded);

        const updateProgress = () => {
          if (audioRef.current && progressRef.current) {
            const percentage =
              (audioRef.current.currentTime / audioRef.current.duration) * 100;
            progressRef.current.style.width = `${percentage}%`;
            animationFrameRef.current = requestAnimationFrame(updateProgress);
          }
        };

        // start updating progress bar
        updateProgress();

        await audioRef.current.play();
      } catch (error) {
        logError("Error playing audio:", error);
      }
    };

    playAudio();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioRef.current) {
        audioRef.current.removeEventListener("playing", handlePlaying);
        audioRef.current.removeEventListener("pause", handlePause);
        audioRef.current.removeEventListener("ended", handleEnded);
        audioRef.current.pause();
        audioRef.current = null;
      }
      dispatch(setIsPlayingAction(false));
    };
  }, [dispatch, media]);

  const handleClose = useCallback(() => {
    dispatch(setMediaAction(null));
  }, [dispatch]);

  if (!media || media.type !== "AudioMediaFile") {
    return null;
  }

  return (
    <>
      <div className="audio-player-component">
        <div className="player">
          <IconButton
            icon={
              <ExternalIcon
                svgName="close"
                size={16}
                color={colors.neutral700}
              />
            }
            onPress={handleClose}
            type="flat"
          />
          <div className="player-title">
            <Text variant="labelSmall">{media.title}</Text>
          </div>
          <div
            className="progress-bar-container"
            style={{ backgroundColor: colors.primary30 }}>
            <div
              ref={progressRef}
              className="progress-bar"
              style={{ backgroundColor: colors.primary60 }}
            />
          </div>
          <IconButton
            icon={
              <ExternalIcon
                svgName={isPlaying ? "pause" : "play"}
                size={16}
                color={colors.neutral700}
              />
            }
            onPress={handlePlayPause}
            type="flat"
          />
          {media.transcript && (
            <IconButton
              icon={
                <ExternalIcon
                  svgName="transcript"
                  size={16}
                  color={colors.neutral700}
                />
              }
              type="flat"
              onPress={handleTranscriptPress}
            />
          )}
        </div>
      </div>
      <AudioTranscript
        transcript={media.transcript}
        isOpen={isTranscriptOpen}
        onClose={handleTranscriptClose}
      />
    </>
  );
};

export { AudioPlayer };
